import { useMutation } from '@apollo/client/react';
import { UPDATE_PHONE_NUMBERS_MUTATION } from '../../integrations/allkort/graphql/gql/PhoneMutations';
import { PhoneTypeId, UpdatePhoneNumbersResponse } from '../../integrations/allkort/graphql/interface/Phones';

/**
 * useUpdatePhone
 *
 * Custom hook for updating an existing phone number on the customer's account.
 * Also triggers a new SMS verification code to be sent to the updated number.
 *
 * @example
 * ```tsx
 * const { updatePhone, loading, error } = useUpdatePhone();
 *
 * const handleUpdate = async () => {
 *   const result = await updatePhone({
 *     phoneId: 12345,
 *     customerId: currentCustomer.id,
 *     description: 'Huvudtelefonnummer',
 *     phoneNo: '+46701234567',
 *     phoneType: 'MAIN',
 *   });
 *   if (result) console.log('Updated:', result);
 * };
 * ```
 */
export const useUpdatePhone = () => {
	const [updatePhoneNumbersMutation, { loading, error, data }] =
		useMutation<UpdatePhoneNumbersResponse>(UPDATE_PHONE_NUMBERS_MUTATION);

	/**
	 * Update an existing phone number on the customer's account.
	 * Triggers a new SMS verification code automatically.
	 * @param phoneId     - The ID of the existing phone number to update
	 * @param customerId  - The customer's ID (currentCustomer.id)
	 * @param description - Label for the number, e.g. "Huvudtelefonnummer"
	 * @param phoneNo     - International format, e.g. "+46701234567"
	 * @param phoneType   - Phone type enum, defaults to 'MAIN'
	 * @param sortOrder   - Display sort order, defaults to 1
	 * @returns The updated phone number list, or null if the mutation failed
	 */
	const updatePhone = async ({
		phoneId,
		customerId,
		description,
		phoneNo,
		phoneType = 'MAIN',
		sortOrder = 1,
	}: {
		phoneId: number
		customerId: number
		description: string
		phoneNo: string
		phoneType?: PhoneTypeId
		sortOrder?: number
	}) => {
		try {
			const result = await updatePhoneNumbersMutation({
				variables: { phoneId, customerId, description, phoneNo, phoneType, sortOrder },
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useUpdatePhone] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.updatePhoneNumbers?.phoneNumber ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { updatePhone, loading, error, data };
};
